import { createSlice } from '@reduxjs/toolkit';
import { leaveApi } from '../../services/leaveApi';

const initialState = {
  leaveRequests: [],
  loading: false,
  error: null,
  filters: {
    status: '',
    leaveType: '',
    search: '',
  },
  pagination: {
    currentPage: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  },
  stats: {
    totalRequests: 0,
    pendingRequests: 0,
    approvedRequests: 0,
    rejectedRequests: 0,
  },
};

const leaveSlice = createSlice({
  name: 'leave',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
      state.pagination.currentPage = 1;
    },
    clearFilters: (state) => {
      state.filters = {
        status: '',
        leaveType: '',
        search: '',
      };
      state.pagination.currentPage = 1;
    },
    setCurrentPage: (state, action) => {
      state.pagination.currentPage = action.payload;
    },
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    setStats: (state, action) => {
      state.stats = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        leaveApi.endpoints.getLeaveRequests.matchFulfilled,
        (state, action) => {
          const { total, page, limit } = action.payload.pagination || {};
          state.pagination.total = total || 0;
          state.pagination.totalPages = Math.ceil((total || 0) / (limit || 10));
          state.pagination.currentPage = page || 1;
        }
      );
  },
});

export const {
  setFilters,
  clearFilters,
  setCurrentPage,
  setPagination,
  setStats,
} = leaveSlice.actions;

// Selectors
export const selectLeaveFilters = (state) => state.leave.filters;
export const selectLeavePagination = (state) => state.leave.pagination;
export const selectLeaveStats = (state) => state.leave.stats;

// Thunks for API integration
export const fetchLeaveRequests = (params) => async (dispatch) => {
  try {
    const result = await dispatch(
      leaveApi.endpoints.getLeaveRequests.initiate(params)
    ).unwrap();

    dispatch(setPagination({
      currentPage: result.pagination?.page || 1,
      total: result.pagination?.total || 0,
      totalPages: Math.ceil((result.pagination?.total || 0) / (result.pagination?.limit || 10)),
    }));

    return result;
  } catch (error) {
    console.error('Error fetching leave requests:', error);
    throw error;
  }
};

export const createLeaveRequest = (leaveData) => async (dispatch) => {
  try {
    const result = await dispatch(
      leaveApi.endpoints.createLeaveRequest.initiate(leaveData)
    ).unwrap();

    return result;
  } catch (error) {
    console.error('Error creating leave request:', error);
    throw error;
  }
};

export const updateLeaveRequest = ({ id, data }) => async (dispatch) => {
  try {
    const result = await dispatch(
      leaveApi.endpoints.updateLeaveRequest.initiate({ id, ...data })
    ).unwrap();

    return result;
  } catch (error) {
    console.error('Error updating leave request:', error);
    throw error;
  }
};

export const approveLeaveRequest = (id) => async (dispatch) => {
  try {
    const result = await dispatch(
      leaveApi.endpoints.approveLeaveRequest.initiate(id)
    ).unwrap();

    return result;
  } catch (error) {
    console.error('Error approving leave request:', error);
    throw error;
  }
};

export const rejectLeaveRequest = (id) => async (dispatch) => {
  try {
    const result = await dispatch(
      leaveApi.endpoints.rejectLeaveRequest.initiate(id)
    ).unwrap();

    return result;
  } catch (error) {
    console.error('Error rejecting leave request:', error);
    throw error;
  }
};

export default leaveSlice.reducer;
